import type { CountryCode, CountryId } from '@indutrust/shared-model';
import { CountryCodes, CountryIds } from '@indutrust/shared-model';

/**
 * Check whether a numeric ID corresponds to a known country.
 *
 * @param countryId The country ID to validate.
 * @returns True if the ID exists in {@link CountryIds} (type-narrowed to {@link CountryId}).
 * @example
 * isValidCountryId(784) // -> true for UAE
 */
export function isValidCountryId(countryId: number): countryId is CountryId {
  return (Object.values(CountryIds) as number[]).includes(countryId);
}

/**
 * Check whether an ISO2 country code corresponds to a known country.
 *
 * @param countryCode The ISO2 code (e.g., "AE").
 * @returns True if the code exists in {@link CountryCodes} (type-narrowed to {@link CountryCode}).
 * @example
 * isValidCountryCode('AE') // -> true
 */
export function isValidCountryCode(
  countryCode: string
): countryCode is CountryCode {
  return (Object.values(CountryCodes) as string[]).includes(countryCode);
}

/**
 * Returns the CDN URL for the SVG flag image of the given ISO 3166-1 alpha-2
 * country code, served by the `purecatamphetamine/country-flag-icons` project.
 *
 * @param code ISO 3166-1 alpha-2 code (e.g. `"AE"`).
 * @example
 * countryFlagUrl('AE') // "https://purecatamphetamine.github.io/country-flag-icons/3x2/AE.svg"
 */
export function countryFlagUrl(code: string): string {
  return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;
}
